/**
 * Client-side breadcrumb buffer.
 *
 * Captures the last N user-visible events (clicks, navigations, fetches,
 * console errors) so when a JS error fires we can attach the breadcrumbs
 * trail to the report. Hugely valuable for triage — instead of "React error
 * #418 happened" you see "user clicked Add To Cart → fetch /api/v2/commerce/cart/add
 * returned 500 → React error fired".
 *
 * Lives entirely in-memory (no storage). Survives single-page-app navigations.
 * Capped at 30 entries so memory stays bounded under runaway loops.
 */

export type BreadcrumbCategory = 'navigation' | 'click' | 'fetch' | 'console' | 'custom';

export interface Breadcrumb {
  ts: number;
  category: BreadcrumbCategory;
  message: string;
  data?: Record<string, unknown>;
}

const MAX = 30;
const buffer: Breadcrumb[] = [];

function push(b: Breadcrumb) {
  buffer.push(b);
  if (buffer.length > MAX) buffer.shift();
}

export function addBreadcrumb(category: BreadcrumbCategory, message: string, data?: Record<string, unknown>): void {
  push({ ts: Date.now(), category, message: message.slice(0, 200), data });
}

export function getBreadcrumbs(): Breadcrumb[] {
  return [...buffer];
}

export function clearBreadcrumbs(): void {
  buffer.length = 0;
}

/**
 * Wire up automatic capture for navigations, clicks, fetches, and console.error.
 * Call once on app boot from a client component (e.g. ErrorReporter).
 */
let installed = false;
export function installBreadcrumbCapture(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // 1. Navigations — patch history methods + listen to popstate
  try {
    const wrap = (method: 'pushState' | 'replaceState') => {
      const original = history[method];
      history[method] = function (this: History, ...args: any[]) {
        addBreadcrumb('navigation', `${method} ${args[2] ?? ''}`);
        return original.apply(this, args as any);
      } as any;
    };
    wrap('pushState');
    wrap('replaceState');
    window.addEventListener('popstate', () => {
      addBreadcrumb('navigation', `popstate ${window.location.pathname}`);
    });
    // Initial page load
    addBreadcrumb('navigation', `loaded ${window.location.pathname}`);
  } catch { /* ignore */ }

  // 2. Clicks — capture target tag + accessible label
  try {
    document.addEventListener(
      'click',
      (ev) => {
        const t = ev.target as HTMLElement | null;
        if (!t) return;
        const tag = (t.tagName || '').toLowerCase();
        const label =
          t.getAttribute('aria-label') ||
          t.getAttribute('data-test-id') ||
          (t.textContent || '').trim().slice(0, 60);
        addBreadcrumb('click', `${tag} "${label}"`);
      },
      // Capture-phase + passive so we never interfere with the actual click
      { capture: true, passive: true }
    );
  } catch { /* ignore */ }

  // 3. Fetches — log URL, method, status, duration
  try {
    const origFetch = window.fetch;
    window.fetch = async function (input: RequestInfo | URL, init?: RequestInit) {
      const start = Date.now();
      const url = typeof input === 'string' ? input : (input as Request).url || String(input);
      const method = (init?.method || (input as Request)?.method || 'GET').toUpperCase();
      try {
        const res = await origFetch.call(this, input as any, init);
        addBreadcrumb('fetch', `${method} ${shortUrl(url)} → ${res.status} (${Date.now() - start}ms)`);
        return res;
      } catch (err) {
        addBreadcrumb('fetch', `${method} ${shortUrl(url)} → network error (${Date.now() - start}ms)`);
        throw err;
      }
    };
  } catch { /* ignore */ }

  // 4. console.error — capture string args (skip Error objects to avoid loops with our own reporter)
  try {
    const origError = console.error.bind(console);
    console.error = function (...args: unknown[]) {
      try {
        const msg = args
          .map((a) => (a instanceof Error ? a.message : typeof a === 'string' ? a : ''))
          .filter(Boolean)
          .join(' ')
          .slice(0, 200);
        if (msg) addBreadcrumb('console', msg);
      } catch { /* ignore */ }
      return origError(...args);
    };
  } catch { /* ignore */ }
}

function shortUrl(u: string): string {
  try {
    const url = new URL(u, window.location.origin);
    return url.pathname + (url.search ? url.search.slice(0, 60) : '');
  } catch {
    return u.slice(0, 100);
  }
}
